import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { kpiService } from '../../services/api';

export const fetchDashboard = createAsyncThunk(
  'kpi/fetchDashboard',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await kpiService.getDashboard(companyId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to load dashboard data' });
    }
  }
);

export const fetchMyKPIs = createAsyncThunk(
  'kpi/fetchMyKPIs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await kpiService.getMyKPIs();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to load KPIs' });
    }
  }
);

export const fetchKPIById = createAsyncThunk(
  'kpi/fetchKPIById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await kpiService.getKPIById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to load KPI' });
    }
  }
);

export const createKPI = createAsyncThunk(
  'kpi/createKPI',
  async (kpiData, { rejectWithValue }) => {
    try {
      const response = await kpiService.createKPI(kpiData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to create KPI' });
    }
  }
);

export const updateKPI = createAsyncThunk(
  'kpi/updateKPI',
  async ({ id, kpiData }, { rejectWithValue }) => {
    try {
      const response = await kpiService.updateKPI(id, kpiData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update KPI' });
    }
  }
);

export const deleteKPI = createAsyncThunk(
  'kpi/deleteKPI',
  async (id, { rejectWithValue }) => {
    try {
      await kpiService.deleteKPI(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to delete KPI' });
    }
  }
);

export const updateTask = createAsyncThunk(
  'kpi/updateTask',
  async ({ kpiId, areaId, taskId, status }, { rejectWithValue }) => {
    try {
      const response = await kpiService.updateTask(kpiId, areaId, taskId, { status });
      return { kpiId, areaId, taskId, task: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update task' });
    }
  }
);

const initialState = {
  dashboard: {
    stats: { totalUsers: 0, avgCompletion: 0, onTrack: 0, needAttention: 0, completed: 0, on_track: 0, in_progress: 0, at_risk: 0, critical: 0, overdue: 0, no_kpi: 0 },
    users: [],
    trend: [],
  },
  myKPIs: [],
  selectedKPI: null,
  loading: false,
  dashboardLoading: false,
  error: null,
};

const kpiSlice = createSlice({
  name: 'kpi',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedKPI: (state) => {
      state.selectedKPI = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dashboard
      .addCase(fetchDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.error = action.payload?.message || 'Failed to load dashboard';
      })
      // Fetch My KPIs
      .addCase(fetchMyKPIs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyKPIs.fulfilled, (state, action) => {
        state.loading = false;
        state.myKPIs = action.payload;
      })
      .addCase(fetchMyKPIs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to load KPIs';
      })
      // Fetch KPI by ID
      .addCase(fetchKPIById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchKPIById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedKPI = action.payload;
      })
      .addCase(fetchKPIById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to load KPI';
      })
      // Create KPI
      .addCase(createKPI.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createKPI.fulfilled, (state, action) => {
        state.loading = false;
        state.myKPIs.push(action.payload);
      })
      .addCase(createKPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to create KPI';
      })
      // Update KPI
      .addCase(updateKPI.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateKPI.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.myKPIs.findIndex(k => k._id === action.payload._id);
        if (index !== -1) state.myKPIs[index] = action.payload;
        if (state.selectedKPI?._id === action.payload._id) state.selectedKPI = action.payload;
      })
      .addCase(updateKPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update KPI';
      })
      // Delete KPI
      .addCase(deleteKPI.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteKPI.fulfilled, (state, action) => {
        state.loading = false;
        state.myKPIs = state.myKPIs.filter(k => k._id !== action.payload);
      })
      .addCase(deleteKPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to delete KPI';
      })
      // Update Task
      .addCase(updateTask.fulfilled, (state, action) => {
        const { kpiId, areaId, taskId, task } = action.payload;
        const kpi = state.myKPIs.find(k => k._id === kpiId);
        if (kpi) {
          const area = kpi.areas.find(a => a._id === areaId);
          if (area) {
            const taskIndex = area.tasks.findIndex(t => t._id === taskId);
            if (taskIndex !== -1) area.tasks[taskIndex] = task;
          }
        }
        if (state.selectedKPI?._id === kpiId) {
          const area = state.selectedKPI.areas.find(a => a._id === areaId);
          if (area) {
            const taskIndex = area.tasks.findIndex(t => t._id === taskId);
            if (taskIndex !== -1) area.tasks[taskIndex] = task;
          }
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to update task';
      });
  },
});

export const { clearError, clearSelectedKPI } = kpiSlice.actions;
export default kpiSlice.reducer;
